import api from "./client";

// Keep in sync with backend/app/schemas/leadhoop.py::LeadhoopSortKey.
export type LeadhoopSortKey =
  | "create_date_desc"
  | "create_date_asc"
  | "status_asc"
  | "revenue_desc"
  | "revenue_asc";

export interface LeadhoopLeadListItem {
  account_slug: string;
  record_id: number;
  publisher_session_id: number;
  publisher_session_name: string | null;
  status: string | null;
  create_date: string | null;
  status_update_date: string | null;
  division: string | null;
  offer: string | null;
  offer_id: string | null;
  revenue: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_1: string | null;
  state: string | null;
  consumer_id: number | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
}

export interface LeadhoopLeadDetail extends LeadhoopLeadListItem {
  external_name: string | null;
  publisher: string | null;
  publisher_id: string | null;
  media_type: string | null;
  offer_type: string | null;
  campaign_type: string | null;
  address: string | null;
  address_2: string | null;
  city: string | null;
  zip: string | null;
  gender: string | null;
  age: number | null;
  dob: string | null;
  ip: string | null;
  signup_url: string | null;
  consent_url: string | null;
  utm_term: string | null;
  utm_content: string | null;
  campus: string | null;
  campus_type: string | null;
  program: string | null;
  degree: string | null;
  education_level: string | null;
  grad_year: number | null;
  disposition: string | null;
  test: boolean | null;
  raw: Record<string, unknown>;
  fetched_at: string;
  updated_at: string;
}

export interface LeadhoopLeadSearchRequest {
  account_slug?: string;
  publisher_session_id?: number;
  status?: string;
  create_date_from?: string;
  create_date_to?: string;
  q?: string;
  sort?: LeadhoopSortKey;
  page?: number;
  page_size?: number;
}

export interface LeadhoopLeadSearchResponse {
  items: LeadhoopLeadListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface LeadhoopAccount {
  slug: string;
  base_url: string;
  publisher_session_ids: number[];
}

export interface LeadhoopSyncRun {
  id: number;
  account_slug: string;
  publisher_session_id: number;
  kind: string;
  started_at: string;
  finished_at: string | null;
  status: string;
  rows_fetched: number;
  rows_inserted: number;
  rows_updated: number;
  error_message: string | null;
  params: Record<string, unknown> | null;
}

export interface LeadhoopStats {
  count_total: number;
  count_by_status: { label: string; count: number }[];
  count_by_account: { label: string; count: number }[];
  count_by_day: { day: string; count: number }[];
}

function cleanBody<T extends object>(body: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(body)) {
    if (v === undefined || v === "" || v === null) continue;
    out[k] = v;
  }
  return out;
}

export async function searchLeadhoopLeads(
  body: LeadhoopLeadSearchRequest,
): Promise<LeadhoopLeadSearchResponse> {
  const { data } = await api.post<LeadhoopLeadSearchResponse>(
    "/api/leadhoop/leads/search",
    cleanBody(body),
  );
  return data;
}

export async function getLeadhoopLead(
  accountSlug: string,
  recordId: number,
): Promise<LeadhoopLeadDetail> {
  const { data } = await api.get<LeadhoopLeadDetail>(
    `/api/leadhoop/leads/${encodeURIComponent(accountSlug)}/${recordId}`,
  );
  return data;
}

export async function getLeadhoopStats(): Promise<LeadhoopStats> {
  const { data } = await api.get<LeadhoopStats>("/api/leadhoop/stats");
  return data;
}

export async function listLeadhoopSyncRuns(params: {
  account_slug?: string;
  publisher_session_id?: number;
  limit?: number;
}): Promise<LeadhoopSyncRun[]> {
  const { data } = await api.get<LeadhoopSyncRun[]>(
    "/api/leadhoop/sync-runs",
    { params: cleanBody(params) },
  );
  return data;
}

export async function triggerLeadhoopSync(body: {
  account_slug: string;
  publisher_session_id: number;
}): Promise<{ sync_run_id: number; status: string }> {
  const { data } = await api.post("/api/leadhoop/sync/trigger", body);
  return data;
}

export async function startLeadhoopBackfill(body: {
  account_slug: string;
  publisher_session_id: number;
  from_date: string;
  to_date: string;
}): Promise<unknown> {
  const { data } = await api.post("/api/leadhoop/backfill/start", body);
  return data;
}

export async function getLeadhoopAccounts(): Promise<LeadhoopAccount[]> {
  const { data } = await api.get<LeadhoopAccount[]>("/api/leadhoop/accounts");
  return data;
}

export async function exportLeadhoopLeadsCsv(
  body: LeadhoopLeadSearchRequest,
): Promise<void> {
  const resp = await api.post("/api/leadhoop/leads/export.csv", cleanBody(body), {
    responseType: "blob",
  });
  const blob = resp.data as Blob;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  link.download = `leadhoop-leads-${ts}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
